import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const DIVS = ["Youth In-Hand", "Adult In-Hand", "Adult Undersaddle"];
const order = (d) => { const i = DIVS.indexOf(d); return i === -1 ? 9 : i; };

const Metric = ({ label, value }) => (
  <div className="rounded-lg bg-muted py-1.5">
    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="font-semibold text-primary">{value ?? "—"}</p>
  </div>
);

const PastRow = ({ h }) => {
  const s = h.archive_snapshot || {};
  const c = s.classes;
  return (
    <Link to={`/mustangs/${h.id}`} data-testid={`past-result-${h.hip_number}`}
      className="block rounded-xl border border-border bg-card p-3 shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="shrink-0 rounded-md bg-primary px-2 py-0.5 text-xs font-bold text-primary-foreground">HIP #{h.hip_number}</span>
          <div className="min-w-0">
            <p className="truncate font-display text-lg font-bold leading-tight text-primary">{h.horse_name}</p>
            <p className="truncate text-xs text-muted-foreground">{h.trainer_name}</p>
          </div>
        </div>
        <p className="shrink-0 font-display text-xl font-bold text-secondary">{s.placing ? `#${s.placing}${s.tied ? " T" : ""}` : "—"}</p>
      </div>
      {c && (
        <div className="mt-2 grid grid-cols-4 gap-2 text-center text-xs">
          <Metric label="H&C" value={c.hc} />
          <Metric label="Trail" value={c.trail} />
          <Metric label="Freestyle" value={c.freestyle} />
          <Metric label="Bonus/Pen" value={`+${s.total_bonus || 0}/-${s.total_penalties || 0}`} />
        </div>
      )}
      <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Overall{s.tied ? " · Tied" : ""}</span>
        <span className="font-display text-xl font-bold text-primary">{s.overall_total ?? "—"}</span>
      </div>
    </Link>
  );
};

// Frozen standings for an archived year, from each horse's archive snapshot.
export const PastYearResults = ({ year, q }) => {
  const [horses, setHorses] = useState(null);
  useEffect(() => {
    setHorses(null);
    api.get("/horses", { params: { year } }).then((r) => setHorses(r.data)).catch(() => setHorses([]));
  }, [year]);
  if (!horses) return null;

  const term = q.trim().toLowerCase().replace(/^#/, "");
  const list = horses.filter((h) => !term || String(h.hip_number) === term
    || h.horse_name?.toLowerCase().includes(term) || h.trainer_name?.toLowerCase().includes(term));
  const groups = [...new Set(list.map((h) => h.division))].sort((a, b) => order(a) - order(b))
    .map((d) => ({ division: d, items: list.filter((h) => h.division === d)
      .sort((a, b) => (a.archive_snapshot?.placing ?? 999) - (b.archive_snapshot?.placing ?? 999) || a.hip_number - b.hip_number) }));

  if (!groups.length) {
    return <p data-testid="past-results-empty" className="mx-4 mt-4 rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">{term ? "No horses match your search." : `No results saved for ${year}.`}</p>;
  }
  return groups.map((g) => (
    <section key={g.division || "none"} className="px-4 pt-4" data-testid={`past-division-${(g.division || "other").toLowerCase().replace(/\s+/g, "-")}`}>
      <h2 className="mb-2 font-display text-lg font-bold text-primary">{g.division || "Other"}</h2>
      <div className="space-y-2">{g.items.map((h) => <PastRow key={h.id} h={h} />)}</div>
    </section>
  ));
};
